<template lang="html">
<div class="monthly-flow-statistics-chart">
    <svg id="flow-duration-monthly-flow" :width="svgWidth" :height="svgHeight">
        <g class="chart" :transform="`translate(${margin.left},${margin.top})`"></g>
    </svg>
    <chart-tooltip
    v-if="tooltipVisible"
    :top="tooltipPosition[1]"
    :left="tooltipPosition[0]"
    :content="tooltipText"
    />
</div>
</template>

<script>
import * as d3 from 'd3';
import numeral from 'numeral';

import ChartTooltip from '@/components/charts/ChartTooltip';
import sciNotation from '@/functions/sciNotationFmt';

export default {
    components: {
        ChartTooltip,
    },
    props: {
        // filtered data from crossfilter group.all()
        data: {
            type: Array,
            required: true,
        },
        // all (unfiltered) data (for preserving scales/ranges)
        dataAll: {
            type: Array,
            required: true,
        },
        // dimension filters from crossfilter dimension.currentFilter()
        dimensionFilter: {
            required: true,
        },
    },
    data() {
        return {
            // @TEMP: make these into props
            svgHeight: (window.innerHeight - 320) / 2,
            svgWidth: (window.innerWidth / 3.34) * 2,
            svg: null,
            g: null,
            margin: {
                top: 35,
                bottom: 35,
                left: 40,
                right: 50,
            },
            height: 225,
            width: 225,
            xScaleTime: null,
            yScaleLog: null,
            barHeight: 4,
            axisLeft: null,
            brush: null,
            initialized: false,
            boxPaddingX: 10, // px padding on either side of each box
            // tooltip props
            tooltipPosition: [0, 0],
            tooltipVisible: false,
            tooltipText: [],
        };
    },
    computed: {
        chartData() {
            return this.formatData(this.data);
        },
        chartDataAll() {
            return this.formatData(this.dataAll);
        },
    },
    mounted() {
        this.initializeSvg();
    },
    methods: {
        /**
         * format data into a structure with all the values needed for the box plot
         * @param  {Array} input array of objects (from crossfilter group.all)
         * @return {Array}       array of objects with values for box & whisker elements
         */
        formatData(input) {
            const output = input.map((e) => {
                // add date object
                const date = new Date(2014, e.key - 1, 1);
                // add percentiles
                const valueList = e.value.valueList;
                const p75 = d3.quantile(valueList, 0.75) || 0;
                const p25 = d3.quantile(valueList, 0.25) || 0;
                return {
                    key: e.key,
                    count: e.value.count,
                    max: e.value.max || 0,
                    median: e.value.median || 0,
                    min: e.value.min || 0,
                    p25,
                    p75,
                    date,
                };
            });

            return output;
        },
        /**
         * initialize svg chart element and define variables
         */
        initializeSvg() {
            // initialize chart & vars
            this.svg = d3.select('#flow-duration-monthly-flow');
            this.g = this.svg.select('g.chart');
            // chart (not element) size
            this.width = +this.svg.attr('width') - this.margin.left - this.margin.right;
            this.height = +this.svg.attr('height') - this.margin.top - this.margin.bottom;
            // initialize scales
            this.xScaleTime = d3.scaleTime().rangeRound([0, this.width]);
            this.yScaleLog = d3.scaleLog().rangeRound([this.height, 0]).clamp(true);
            // transition for bar size and axis
            this.transition = d3.transition().duration(500);
            // set x-scale input domain (12 months)
            this.xScaleTime.domain([
                // 2014 - any non-leap year
                new Date(2014, 0, 1),
                new Date(2014, 11, 31),
            ]);

            // adjust scales: update x-scale input domain
            const xMax = d3.max(this.chartDataAll, d => d.max);
            this.yScaleLog.domain([
                0.001,
                // use dataAll, don't change domain
                d3.max([0.001, xMax]),
            ]).nice();

            // bottom axis
            this.g.append('g')
            .attr('class', 'axis axis--x')
            .attr('transform', `translate(0,${this.height})`)
            .call(d3.axisBottom(this.xScaleTime).tickFormat(d3.timeFormat('%B')))
            .selectAll('.tick text')
            .call(this.moveAxisLabel);
            // left axis
            this.axisLeft = d3.axisLeft(this.yScaleLog)
            .ticks(3)
            .tickFormat(sciNotation);
            this.g.append('g')
            .attr('class', 'axis axis--y')
            .attr('transform', 'translate(-2,0)');

            // add brush
            this.brush = d3.brushX()
            .extent([[0, 0], [this.width, this.height]])
            .on('end', this.brushEnded);

            // define & update bars
            this.updateChart();

            // axis label
            this.g.append('text')
            .attr('class', 'axis-label')
            .attr('transform', 'translate(3,3) rotate(-90)')
            .attr('y', 6)
            .attr('dy', '0.71em')
            .attr('text-anchor', 'end')
            .text('Monthly Flow (m³/s)');

            // add selection brush
            this.g.append('g')
            .attr('class', 'brush monthly-flow')
            .call(this.brush);

            // track mouse events on the chart area
            this.g.on('mousemove', this.mouseMove);
            this.g.on('mouseout', this.mouseOut);

            this.initialized = true;
        },
        /**
         * center a time axis label between ticks
         * @param  {[type]}  text [description]
         * @return {Boolean}      [description]
         */
        moveAxisLabel(text) {
            text.attr('x', (date) => {
                const thisDate = date;
                const nextDate = d3.timeMonth.offset(date, 1);
                const d0 = this.xScaleTime(thisDate);
                const d1 = this.xScaleTime(nextDate);
                const width = d1 - d0;
                return width / 2;
            });
        },
        /**
         * update chart bars & axis when data changes
         */
        updateChart() {
            // update x-axis
            this.g.select('.axis--y')
            .transition(this.transition)
            .call(this.axisLeft);

            // d3 data join to create column groups
            const column = this.g.selectAll('.column')
            .data(this.chartData);

            const boxWidth = (this.width / 12) - (this.boxPaddingX * 2);

            // add group elements
            const gCols = column.enter().append('g')
            .attr('class', 'column')
            .attr('transform', (d) => {
                const x = this.xScaleTime(d.date) + this.boxPaddingX;
                return `translate(${x},0)`;
            });

            // add center line
            gCols.append('line')
            .attr('class', 'whisker center-line')
            .attr('x1', boxWidth / 2)
            .attr('x2', boxWidth / 2);

            // add whiskers showing min/max
            gCols.append('line')
            .attr('class', 'min-max min')
            .attr('x1', 0)
            .attr('x2', boxWidth);
            gCols.append('line')
            .attr('class', 'min-max max')
            .attr('x1', 0)
            .attr('x2', boxWidth);

            // add innerquartile box
            gCols.append('rect')
            .attr('class', 'box')
            .attr('width', boxWidth);

            // add median line
            gCols.append('line')
            .attr('class', 'median')
            .attr('x1', 0)
            .attr('x2', boxWidth);

            // data join on whiskers and boxes
            const minLine = this.g.selectAll('.min-max.min')
            .data(this.chartData);
            const maxLine = this.g.selectAll('.min-max.max')
            .data(this.chartData);
            const centerLine = this.g.selectAll('.whisker.center-line')
            .data(this.chartData);
            const box = this.g.selectAll('.box')
            .data(this.chartData);
            const median = this.g.selectAll('.median')
            .data(this.chartData);

            // update boxes
            box
            .transition(this.transition)
            .attr('y', d => this.yScaleLog(d.p75))
            .attr('height', (d) => {
                const p75 = this.yScaleLog(d.p75);
                const p25 = this.yScaleLog(d.p25);
                return p25 - p75;
            });

            // update whiskers / center lines
            minLine
            .transition(this.transition)
            .attr('y1', d => this.yScaleLog(d.min))
            .attr('y2', d => this.yScaleLog(d.min));
            maxLine
            .transition(this.transition)
            .attr('y1', d => this.yScaleLog(d.max))
            .attr('y2', d => this.yScaleLog(d.max));
            centerLine
            .transition(this.transition)
            .attr('y1', d => this.yScaleLog(d.max))
            .attr('y2', d => this.yScaleLog(d.min));

            // update medians
            median
            .transition(this.transition)
            .attr('y1', d => this.yScaleLog(d.median))
            .attr('y2', d => this.yScaleLog(d.median));
        },
        /**
         * callback for d3 brush end selection events
         * calculates the month range of the selection
         */
        brushEnded() {
            if (!d3.event.sourceEvent) return; // Only transition after input.
            // clear filters for empty selection
            const s = d3.event.selection;
            if (!s) {
                this.clearFilters();
                return;
            }

            // date values
            const d0 = s.map(this.xScaleTime.invert);
            // rounded to the nearest month
            const d1 = d0.map(d3.timeMonth.round);

            // if empty when rounded, use floor & ceil instead
            if (d1[0] >= d1[1]) {
                d1[0] = d3.timeMonth.floor(d0[0]);
                d1[1] = d3.timeMonth.offset(d1[0]);
            }

            d3.select('.brush.monthly-flow')
            .transition(this.transition)
            .call(d3.event.target.move, d1.map(this.xScaleTime));

            this.updateFilters(d1);
        },
        /**
         * emit an event with a new filter range
         * @param  {Array} dates array with min and max years
         */
        updateFilters(dates) {
            const months = dates.map(m => +d3.timeFormat('%m')(m));
            // crossfilter is just using number 1-12,
            // so "next january" is just month "13"
            if (months[1] === 1) {
                months[1] = 13;
            }
            this.$emit('updateFilters', months);
        },
        /**
         * emit null to clear dimension filters
         */
        clearFilters() {
            this.$emit('updateFilters', null);
        },
        /**
         * move brush to reflect changed filters
         * pass null to clear brush
         * @param  {Array|null} months params for group.filter method
         */
        setBrush(selection) {
            // clear brush on null (no transition)
            if (selection === null) {
                d3.select('.brush.monthly-flow')
                .call(this.brush.move, null);
                return;
            }
            // convert month integer to pixel values
            const selectionRange = selection.map((m) => {
                // adjust month for js Date constructor (0-11)
                // 12 will automatically be next january
                const date = new Date(2014, m - 1, 1);
                return this.xScaleTime(date);
            });
            // move brush
            d3.select('.brush.monthly-flow')
            .transition(this.transition)
            .call(this.brush.move, selectionRange);
        },
        /**
         * update tooltip when the mouse moves
         */
        mouseMove() {
            const [gX, gY] = d3.mouse(this.g.node());
            // hide on mouseout
            if (gX < 0 || gX > this.width || gY < 0 || gY > this.height) {
                this.mouseOut();
                return;
            }

            // position relative to the root flow duration tool element
            this.tooltipPosition = d3.mouse(this.$parent.$el);
            const date = this.xScaleTime.invert(gX);
            const month = d3.timeMonth.floor(date);
            const monthString = d3.timeFormat('%B')(month);
            const monthNum = +d3.timeFormat('%m')(month);

            const monthData = this.chartData.find(d => d.key === monthNum);
            if (!monthData) {
                this.mouseOut();
                return;
            }

            let { min, max, median, p25, p75 } = monthData;
            min = numeral(min).format('0,0.[00]');
            max = numeral(max).format('0,0.[00]');
            median = numeral(median).format('0,0.[00]');
            p25 = numeral(p25).format('0,0.[00]');
            p75 = numeral(p75).format('0,0.[00]');
            this.tooltipText = [
                { text: monthString, class: 'headline' },
                { text: 'Discharge (m³/s)', class: 'subheading' },
                { label: 'Max', value: max, class: 'black white--text' },
                { label: '75th %ile', value: p75, class: 'orange' },
                { label: 'Median', value: median, class: 'black white--text' },
                { label: '25th %ile', value: p25, class: 'orange' },
                { label: 'Min', value: min, class: 'black white--text' },
            ];
            this.tooltipVisible = true;
        },
        /**
         * hide the tooltip on mouseout
         */
        mouseOut() {
            this.tooltipVisible = false;
        },
    },
    watch: {
        // update chart when data changes
        data: {
            handler() {
                this.updateChart();
            },
            deep: true,
        },
        // update brush selection when cf filter changes externally
        dimensionFilter(selection) {
            this.setBrush(selection);
        },
    },
};
</script>

<style lang="scss">
#flow-duration-monthly-flow {

    .overlay {
        pointer-events: all;
    }

    rect, line {
        shape-rendering: crispEdges;
    }

    line.center-line,
    line.median,
    line.min,
    line.max {
        stroke-width: 2px;
        stroke: #000;
    }

    line.center-line {
        stroke-dasharray: 3 3;
    }

    rect.box {
        fill: #FF9800;
        stroke-width: 2px;
        stroke: #000;
    }

    // axis label text
    .axis-label {
        shape-rendering: optimizeLegibility;
        user-select: none;
        font-weight: 500;
        fill: #000;
        text-shadow: 0 0 1px #fff,
            -1px 0 1px #fff,
            1px 0 1px #fff;
    }

    // axis ticks
    g.axis {
        user-select: none;
    }

    // brush styles
    .brush {
        .selection {
            fill: rgba(#81D4FA, .5);
            fill-opacity: 1;
            stroke: none;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/charts/FDMonthlyFlowStats.vue